// Vocal-Tuning-Editor (wie Melodyne): Doppelklick auf einen Audio-Clip öffnet ihn unten im Dock.
// Erkannte Noten als Blöcke über einem Halbtonraster, Tonhöhenverlauf als Linie (grau: gesungen, farbig: nach Korrektur).
// Automatisch: alle (oder die gewählten) Noten auf die Tonart des Songs ziehen, Drift glätten, Vibrato abschwächen.
// Von Hand: Noten ziehen (rastet auf Halbtöne, ⌥ frei), Doppelklick legt eine Note auf die Tonart, ⌫ setzt zurück.
// Gerechnet wird über Elastic Audio (PSOLA, Klangfarbe bleibt), jede Änderung ist ein Schritt für Rückgängig.
import { useEffect, useRef, useState } from 'react';
import { inKey, parseKey, type Clip, type Note, type Project } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { Panel, toast } from '../components/ui.tsx';
import { elasticBusy, onElasticChange, pitchOf, pitchOfNow } from '../audio/elastic.ts';
import { clipMap, ensureWarp } from '../edit/warp.ts';
import { setElastic } from '../edit/elasticops.ts';
import { TUNE_PRESETS, correctNotes, estimateKey, midiOf, noteMedian, refOffset, segmentNotes, targetFor, tuneCurve, type TuneNote } from '../edit/pitch.ts';
import { beatAt } from '@srs/shared';
import { emptyTrack, uid } from '../state/demo.ts';
import { noteName, pianoFocus } from './PianoRoll.tsx';

const KEYS = 52, RULER = 22;
type Tune = NonNullable<Clip['tune']>;
type Drag = { kind: 'move'; ids: number[]; y0: number; base: number[]; dy: number; fine: boolean } | { kind: 'band'; x0: number; y0: number; x1: number; y1: number; add: number[] } | null;

export function TuneEditor({ clipId, onClose }: { clipId: string; onClose: () => void }) {
  const clip = useStudio((s) => s.project.clips.find((c) => c.id === clipId));
  const track = useStudio((s) => s.project.tracks.find((t) => t.id === clip?.trackId));
  const songKey = useStudio((s) => s.project.key);
  const commit = useStudio((s) => s.commit);
  const wrap = useRef<HTMLDivElement>(null), canvas = useRef<HTMLCanvasElement>(null);
  const view = useRef({ t0: 0, pps: 160, top: 72, rowH: 14, init: false });
  const sel = useRef<Set<number>>(new Set());
  const drag = useRef<Drag>(null);
  const active = useRef(true);
  const [detected, setDetected] = useState<TuneNote[] | null>(null);
  const [strength, setStrength] = useState(Math.round(TUNE_PRESETS.natural.strength * 100));
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  useEffect(() => { if (!clip) onClose(); }, [clip, onClose]);
  // Tonhöhe der Quelle erkennen (einmal je Quelle, im Hintergrund)
  useEffect(() => {
    if (!clip?.sourceId) return;
    let live = true;
    void pitchOf(clip.sourceId).then((tr) => {
      if (!live || !tr) return;
      const p = P(), c = cur(); if (!c) return;
      const m = clipMap(p, c), a = m.toSrc(c.start), b = m.toSrc(c.start + c.duration);
      setDetected(segmentNotes(tr).filter((n) => n.s1 > a && n.s0 < b).map((n) => ({ ...n, to: n.m })));
    });
    const off = onElasticChange(rerender);
    return () => { live = false; off(); };
  }, [clip?.sourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const P = () => useStudio.getState().project;
  const cur = () => P().clips.find((c) => c.id === clipId);
  /** aktuelle Noten: gespeichertes Tuning, sonst die erkannten */
  // Standard ist die Voreinstellung „Natürlich“ (Rückmeldung: klingt am besten)
  const N = TUNE_PRESETS.natural;
  const tune = (): Tune => cur()?.tune ?? { notes: detected ?? [], drift: N.drift, vibrato: N.vibrato, speed: N.speed, humanize: N.humanize, flex: N.flex, keep: N.keep };
  /** Änderung am Tuning als Schritt; schaltet Elastic (Monophon) ein, falls nötig */
  const edit = (label: string, f: (t: Tune) => void) => {
    const base = structuredClone(tune());
    if (!base.notes.length) return toast('Noch keine Noten erkannt');
    commit(label, (q) => {
      const c = q.clips.find((x) => x.id === clipId), t = q.tracks.find((x) => x.id === c?.trackId);
      if (!c || !t) return;
      if (!t.elastic) setElastic(q, t.id, 'monophonic');
      ensureWarp(q, c);
      c.tune = c.tune ?? base;
      f(c.tune);
    });
  };

  // Tonart fürs Tuning: eigene Wahl am Clip, sonst die des Songs; „chromatisch“ zieht auf den nächsten Halbton
  // Standard wie bei Auto-Tune und Melodyne: nächster Halbton (chromatisch); Tonart des Songs oder eine erkannte wählbar
  const keyOpt = tune().key, key = !keyOpt || keyOpt === 'chromatic' ? 'chromatic' : keyOpt === 'song' ? songKey : keyOpt, chromatic = key === 'chromatic';
  const est = tune().notes.length >= 8 ? estimateKey(tune().notes) : null;
  const ref = tune().ref ?? 440;
  const snap = (m: number, k = 1, flex = 0) => targetFor(m, key, { strength: k, flex, ref });
  const inScale = (m: number) => chromatic || inKey(m, key);
  const keyLabel = (k: string) => (k === 'chromatic' ? 'chromatisch' : k);

  // ---- Koordinaten ----
  const X = (t: number) => KEYS + (t - view.current.t0) * view.current.pps;
  const T = (x: number) => view.current.t0 + (x - KEYS) / view.current.pps;
  const Y = (m: number) => RULER + (view.current.top - m) * view.current.rowH;
  const M = (y: number) => view.current.top - (y - RULER) / view.current.rowH;
  const noteRect = (p: Project, c: Clip, n: TuneNote, to = n.to) => {
    const map = clipMap(p, c), x0 = X(map.toTime(n.s0)), x1 = X(map.toTime(n.s1)), y = Y(Math.round(to * 100) / 100 + 0.5);
    return { x0, x1, y, h: view.current.rowH - 2 };
  };
  const shownTo = (i: number, n: TuneNote) => {
    const d = drag.current;
    if (d?.kind !== 'move' || !d.ids.includes(i)) return n.to;
    const k = d.ids.indexOf(i), raw = d.base[k] + -d.dy / view.current.rowH;
    return d.fine ? raw : Math.round(raw);
  };

  // ---- Zeichnen ----
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const cv = canvas.current, w = wrap.current, c = cur(), p = P();
      if (cv && w && c) {
        const dpr = devicePixelRatio || 1, W = w.clientWidth, H = w.clientHeight;
        if (cv.width !== W * dpr || cv.height !== H * dpr) { cv.width = W * dpr; cv.height = H * dpr; cv.style.width = `${W}px`; cv.style.height = `${H}px`; }
        const g = cv.getContext('2d')!; g.setTransform(dpr, 0, 0, dpr, 0, 0);
        paint(g, W, H, p, c);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  });

  function paint(g: CanvasRenderingContext2D, W: number, H: number, p: Project, c: Clip) {
    const v = view.current, tn = tune(), notes = tn.notes, col = track?.color ?? '#003399';
    if (!v.init && notes.length) {
      const ms = notes.map((n) => n.m), lo = Math.min(...ms), hi = Math.max(...ms);
      v.rowH = Math.max(10, Math.min(22, (H - RULER - 10) / Math.max(12, hi - lo + 6)));
      v.top = Math.ceil(hi + (H - RULER) / v.rowH / 2 - (hi - lo) / 2);
      v.t0 = c.start - 0.2; v.pps = Math.max(40, (W - KEYS - 20) / Math.max(1, c.duration + 0.4)); v.init = true;
    }
    g.clearRect(0, 0, W, H);
    const { tonic } = parseKey(chromatic ? songKey : key);
    // Halbtonzeilen: Töne der Tonart hell, andere dunkler, Grundton markiert
    for (let m = Math.floor(M(H)); m <= Math.ceil(M(RULER)); m++) {
      const y = Y(m + 0.5), ink = inScale(m);
      g.fillStyle = ink ? '#ffffff' : '#f1f3f7'; g.fillRect(KEYS, y, W - KEYS, v.rowH);
      g.fillStyle = '#e6e9ef'; g.fillRect(KEYS, y + v.rowH - 0.5, W - KEYS, 0.5);
      g.fillStyle = (((m - tonic) % 12) + 12) % 12 === 0 ? '#dfe7f7' : ink ? '#fafbfc' : '#e4e7ed'; g.fillRect(0, y, KEYS, v.rowH);
      if (v.rowH >= 11) { g.font = '600 9.5px "IBM Plex Mono"'; g.fillStyle = ink ? '#071026' : '#8a93a6'; g.fillText(noteName(m), 5, y + v.rowH - 3.5); }
    }
    g.fillStyle = '#d5dae3'; g.fillRect(KEYS - 1, RULER, 1, H);
    // Clip-Bereich
    g.fillStyle = 'rgba(7,16,38,.03)'; g.fillRect(KEYS, RULER, Math.max(0, X(c.start) - KEYS), H); g.fillRect(X(c.start + c.duration), RULER, W, H);
    // Lineal: Sekunden ab Clip-Anfang
    g.fillStyle = '#f7f8fa'; g.fillRect(KEYS, 0, W, RULER); g.fillStyle = '#d5dae3'; g.fillRect(KEYS, RULER - 1, W, 1);
    const step = v.pps > 300 ? 0.25 : v.pps > 120 ? 0.5 : v.pps > 50 ? 1 : 2;
    g.font = '10px "IBM Plex Mono"'; g.fillStyle = '#5b6b8c';
    for (let s = Math.floor((T(KEYS) - c.start) / step) * step; X(c.start + s) < W; s += step) { const x = Math.round(X(c.start + s)) + 0.5; if (x < KEYS) continue; g.fillRect(x, RULER - 6, 1, 6); g.fillText(`${s.toFixed(step < 1 ? 2 : 0)} s`, x + 3, 13); }
    const map = clipMap(p, c), tr = c.sourceId ? pitchOfNow(c.sourceId) : null;
    // Noten: Ziel als Block, erkannte Lage gestrichelt, wenn verschoben
    notes.forEach((n, i) => {
      const to = shownTo(i, n), r = noteRect(p, c, n, to), on = sel.current.has(i);
      if (r.x1 < KEYS || r.x0 > W) return;
      if (Math.abs(to - n.m) > 0.02) { const r0 = noteRect(p, c, n, n.m); g.setLineDash([3, 3]); g.strokeStyle = 'rgba(7,16,38,.35)'; g.lineWidth = 1; g.strokeRect(r0.x0 + 0.5, r0.y + 1.5, r0.x1 - r0.x0 - 1, r0.h - 1); g.setLineDash([]); }
      rr(g, r.x0, r.y + 1, Math.max(3, r.x1 - r.x0), r.h, 4);
      if (n.off) { g.fillStyle = on ? 'rgba(7,16,38,.22)' : 'rgba(7,16,38,.08)'; g.fill(); g.setLineDash([2, 2]); g.strokeStyle = '#8a93a6'; g.lineWidth = 1; g.stroke(); g.setLineDash([]); }
      else { g.fillStyle = on ? shade(col, 0.75) : hexA(col, 0.55); g.fill(); g.strokeStyle = Math.abs(to - n.m) > 2 ? '#e23e2c' : on ? '#071026' : shade(col, 0.7); g.lineWidth = on || Math.abs(to - n.m) > 2 ? 1.5 : 1; g.stroke(); }
      if (r.x1 - r.x0 > 34 && v.rowH >= 10) { g.font = '600 9.5px "IBM Plex Sans"'; g.fillStyle = n.off ? '#5b6b8c' : '#fff'; const dc = Math.round((to - Math.round(to)) * 100); g.fillText(n.off ? 'aus' : `${noteName(Math.round(to))}${dc ? ` ${dc > 0 ? '+' : ''}${dc}` : ''}`, r.x0 + 4, r.y + r.h - 2.5); }
    });
    // Tonhöhenverlauf: gesungen (grau) und nach der Korrektur (Spurfarbe)
    if (tr) {
      const curve = tuneCurve(tr, notes.map((n, i) => ({ ...n, to: shownTo(i, n) })), tn);
      for (const pass of [0, 1]) {
        g.beginPath(); let pen = false;
        for (let k = 0; k < tr.f0.length; k++) {
          const s = tr.off + k * tr.hop, t = map.toTime(s), x = X(t);
          if (x < KEYS || x > W || !tr.f0[k] || t < c.start || t > c.start + c.duration) { pen = false; continue; }
          const y = Y(midiOf(tr.f0[k]) + (pass ? curve[k] : 0));
          if (pen) g.lineTo(x, y); else g.moveTo(x, y); pen = true;
        }
        g.strokeStyle = pass ? shade(col, 0.6) : 'rgba(7,16,38,.28)'; g.lineWidth = pass ? 1.6 : 1; g.stroke();
      }
    } else { g.font = '600 12px "IBM Plex Sans"'; g.fillStyle = '#5b6b8c'; g.fillText('Tonhöhe wird erkannt …', KEYS + 16, RULER + 24); }
    // Gummiband
    const d = drag.current;
    if (d?.kind === 'band') { g.fillStyle = 'rgba(0,51,153,.08)'; g.strokeStyle = '#003399'; g.lineWidth = 1; const x = Math.min(d.x0, d.x1), y = Math.min(d.y0, d.y1); g.fillRect(x, y, Math.abs(d.x1 - d.x0), Math.abs(d.y1 - d.y0)); g.strokeRect(x + 0.5, y + 0.5, Math.abs(d.x1 - d.x0), Math.abs(d.y1 - d.y0)); }
    // Abspielkopf
    const px = X(engine.position);
    if (px >= KEYS && px <= W) { g.fillStyle = '#003399'; g.fillRect(Math.round(px), 0, 1.5, H); }
    if (elasticBusy() && c.tune) { g.font = '600 10.5px "IBM Plex Sans"'; const txt = 'Korrektur wird berechnet …', tw = g.measureText(txt).width; rr(g, W - tw - 22, RULER + 6, tw + 14, 18, 4); g.fillStyle = '#071026'; g.fill(); g.fillStyle = '#fff'; g.fillText(txt, W - tw - 15, RULER + 18.5); }
  }

  // ---- Maus ----
  const local = (e: { clientX: number; clientY: number }) => { const r = wrap.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const hit = (x: number, y: number) => { const p = P(), c = cur(); if (!c) return -1; const ns = tune().notes; for (let i = ns.length - 1; i >= 0; i--) { const r = noteRect(p, c, ns[i]); if (x >= r.x0 && x <= r.x1 && y >= r.y && y <= r.y + r.h + 2) return i; } return -1; };
  const onDown = (e: React.PointerEvent) => {
    const { x, y } = local(e);
    (e.target as Element).setPointerCapture(e.pointerId);
    if (y < RULER) { engine.locate(Math.max(0, T(x))); return; }
    const i = hit(x, y), ns = tune().notes;
    if (i >= 0) {
      if (e.shiftKey) { if (sel.current.has(i)) sel.current.delete(i); else sel.current.add(i); }
      else if (!sel.current.has(i)) sel.current = new Set([i]);
      const ids = [...sel.current];
      drag.current = { kind: 'move', ids, y0: e.clientY, base: ids.map((k) => ns[k].to), dy: 0, fine: e.altKey };
    } else {
      if (!e.shiftKey) sel.current = new Set();
      drag.current = { kind: 'band', x0: x, y0: y, x1: x, y1: y, add: [...sel.current] };
    }
    rerender();
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current; if (!d) return;
    const { x, y } = local(e);
    if (d.kind === 'move') { d.dy = e.clientY - d.y0; d.fine = e.altKey; }
    else {
      d.x1 = x; d.y1 = y;
      const p = P(), c = cur()!, a = Math.min(d.x0, x), b = Math.max(d.x0, x), lo = Math.min(d.y0, y), hi = Math.max(d.y0, y);
      sel.current = new Set([...d.add, ...tune().notes.map((n, i) => ({ r: noteRect(p, c, n), i })).filter(({ r }) => r.x1 > a && r.x0 < b && r.y + r.h > lo && r.y < hi).map(({ i }) => i)]);
    }
  };
  const onUp = () => {
    const d = drag.current; drag.current = null;
    if (d?.kind === 'move' && Math.abs(d.dy) > 2) {
      const vals = d.ids.map((i, k) => { const raw = d.base[k] - d.dy / view.current.rowH; return d.fine ? Math.round(raw * 100) / 100 : Math.round(raw); });
      edit('Note verschieben', (t) => { d.ids.forEach((i, k) => { if (t.notes[i]) t.notes[i].to = vals[k]; }); });
    }
    rerender();
  };
  const onDouble = (e: React.MouseEvent) => {
    const { x, y } = local(e), i = hit(x, y);
    if (i < 0) return;
    edit('Note auf Tonart', (t) => { t.notes[i].to = Math.round(snap(t.notes[i].m) * 100) / 100; delete t.notes[i].off; });
  };
  const onWheel = (e: React.WheelEvent) => {
    const v = view.current;
    if (e.ctrlKey || e.metaKey) { const { x } = local(e), t = T(x); v.pps = Math.max(20, Math.min(2000, v.pps * (e.deltaY < 0 ? 1.15 : 1 / 1.15))); v.t0 = t - (x - KEYS) / v.pps; }
    else if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) v.t0 += (e.deltaX || e.deltaY) / v.pps;
    else v.top -= Math.sign(e.deltaY) * Math.max(1, Math.round(Math.abs(e.deltaY) / 40));
  };

  // ---- Befehle ----
  const targets = () => { const n = tune().notes.length; return sel.current.size ? [...sel.current].filter((i) => i < n) : Array.from({ length: n }, (_, i) => i); };
  const correct = (k0?: number) => {
    const ids = targets().filter((i) => !tune().notes[i].off), k = k0 ?? strength / 100, flex = tune().flex ?? 0;
    let left = 0;
    edit(`Korrigieren (${Math.round(k * 100)} %)`, (t) => {
      const all = correctNotes(t.notes, key, { strength: k, flex, ref, keep: t.keep ?? 0 });
      for (const i of ids) { const v = Math.round(all[i] * 100) / 100; if (v === Math.round(t.notes[i].m * 100) / 100 && flex) left++; t.notes[i].to = v; }
    });
    toast(`${ids.length - left} ${ids.length - left === 1 ? 'Note' : 'Noten'} auf ${chromatic ? 'den nächsten Halbton' : key} gezogen${k < 1 ? ` (${Math.round(k * 100)} %)` : ''}${left ? `, ${left} weit weg gelassen (Flex-Tune)` : ''}. Einzelne Noten lassen sich jetzt von Hand ziehen.`, 5000);
  };
  /** Voreinstellung: Einstellungen setzen und gleich korrigieren */
  const preset = (id: keyof typeof TUNE_PRESETS) => {
    const pr = TUNE_PRESETS[id];
    edit(`Voreinstellung ${pr.label}`, (t) => { t.drift = pr.drift; t.vibrato = pr.vibrato; t.speed = pr.speed; t.humanize = pr.humanize; t.flex = pr.flex; t.keep = pr.keep; });
    setStrength(Math.round(pr.strength * 100));
    correct(pr.strength);
  };
  const bypass = () => { const ids = [...sel.current]; if (!ids.length) return toast('Erst Noten wählen'); const on = !ids.every((i) => tune().notes[i]?.off); edit(on ? 'Noten auslassen' : 'Noten wieder korrigieren', (t) => { for (const i of ids) { if (on) t.notes[i].off = true; else delete t.notes[i].off; } }); };
  /** Note an der Abspielposition teilen (Melodyne: Notentrennwerkzeug) */
  const split = () => {
    const c = cur(), tr = c?.sourceId ? pitchOfNow(c.sourceId) : null; if (!c || !tr) return;
    const s = clipMap(P(), c).toSrc(engine.position), ns = tune().notes, i = ns.findIndex((n) => s > n.s0 + 0.03 && s < n.s1 - 0.03);
    if (i < 0) return toast('Abspielposition in eine Note setzen (Klick ins Lineal), dann S');
    edit('Note teilen', (t) => {
      const n = t.notes[i], d = n.to - n.m, a = { ...n, s1: s, m: noteMedian(tr, n.s0, s) }, b = { ...n, s0: s, m: noteMedian(tr, s, n.s1) };
      a.to = Math.round((a.m + d) * 100) / 100; b.to = Math.round((b.m + d) * 100) / 100;
      if (Number.isNaN(a.m)) { a.m = n.m; a.to = n.to; } if (Number.isNaN(b.m)) { b.m = n.m; b.to = n.to; }
      t.notes.splice(i, 1, a, b);
    });
    sel.current = new Set([i, i + 1]);
  };
  /** Gewählte benachbarte Noten verbinden */
  const join = () => {
    const c = cur(), tr = c?.sourceId ? pitchOfNow(c.sourceId) : null; if (!c || !tr) return;
    const ids = [...sel.current].sort((a, b) => a - b);
    if (ids.length < 2) return toast('Mindestens zwei benachbarte Noten wählen');
    edit('Noten verbinden', (t) => {
      const ns = t.notes.slice(ids[0], ids[ids.length - 1] + 1), s0 = ns[0].s0, s1 = ns[ns.length - 1].s1, m = noteMedian(tr, s0, s1);
      const n = { s0, s1, m: Number.isNaN(m) ? ns[0].m : m, to: 0 }; n.to = Math.round((n.m + (ns[0].to - ns[0].m)) * 100) / 100;
      t.notes.splice(ids[0], ns.length, n);
    });
    sel.current = new Set([ids[0]]);
  };
  /** Melodie als MIDI-Spur (gerundete Zieltöne, ohne ausgelassene Noten) */
  const toMidi = () => {
    const c = cur(), tk = track; if (!c || !tk) return;
    const p = P(), map = clipMap(p, c), b0 = beatAt(p, c.start), off = refOffset(ref);
    const notes: Note[] = tune().notes.filter((n) => !n.off).map((n) => { const t0 = map.toTime(n.s0), t1 = map.toTime(n.s1); return { t: Math.round((beatAt(p, t0) - b0) * 1000) / 1000, d: Math.max(0.05, Math.round((beatAt(p, t1) - beatAt(p, t0)) * 1000) / 1000), m: Math.round(n.to - off), v: 96, by: { userId: useStudio.getState().user.id, at: new Date().toISOString() } }; }).filter((n) => n.t >= 0);
    if (!notes.length) return toast('Keine Noten');
    const id = uid('t');
    commit('Melodie als MIDI', (q) => {
      const i = q.tracks.findIndex((x) => x.id === tk.id);
      q.tracks.splice(i + 1, 0, { ...emptyTrack(`${tk.name} Melodie`, 'midi', tk.color, { instrument: { plugin: 'srs:synth', preset: 'lead' } }), id });
      q.clips.push({ id: uid('c'), trackId: id, kind: 'midi', name: `${c.name} Melodie`, start: c.start, offset: 0, duration: c.duration, notes, fadeIn: 0, fadeOut: 0, by: { userId: useStudio.getState().user.id, at: new Date().toISOString() } });
    });
    toast(`${notes.length} Noten als MIDI-Spur „${tk.name} Melodie“ angelegt`);
  };
  const reset = () => { const ids = targets(); edit('Tuning zurücksetzen', (t) => { for (const i of ids) t.notes[i].to = t.notes[i].m; }); };
  const setOpt = (k: 'drift' | 'vibrato' | 'speed' | 'humanize' | 'flex' | 'ref' | 'keep', v: number) => edit(({ drift: 'Drift', vibrato: 'Vibrato', speed: 'Übergang', humanize: 'Humanize', flex: 'Flex-Tune', ref: 'Kammerton', keep: 'Intervalle halten' })[k], (t) => { t[k] = v; });
  const redetect = () => {
    const c = cur(); if (!c?.sourceId) return;
    void pitchOf(c.sourceId).then((tr) => {
      if (!tr) return;
      const p = P(), m = clipMap(p, c), a = m.toSrc(c.start), b = m.toSrc(c.start + c.duration);
      const ns = segmentNotes(tr).filter((n) => n.s1 > a && n.s0 < b).map((n) => ({ ...n, to: n.m }));
      setDetected(ns); sel.current = new Set();
      if (c.tune) commit('Noten neu erkennen', (q) => { const x = q.clips.find((y) => y.id === clipId); if (x?.tune) x.tune.notes = ns; });
    });
  };

  // ---- Tastatur ----
  useEffect(() => {
    const onPointer = (e: PointerEvent) => { active.current = !!wrap.current?.closest('.panel')?.contains(e.target as Node); pianoFocus.active = active.current; };
    const onKey = (e: KeyboardEvent) => {
      if (!active.current || (e.target as HTMLElement).closest('input, textarea, select')) return;
      const mod = e.metaKey || e.ctrlKey, stop = () => { e.preventDefault(); e.stopImmediatePropagation(); };
      const ids = [...sel.current];
      if (e.key === 'Escape') { stop(); if (sel.current.size) { sel.current = new Set(); rerender(); } else onClose(); }
      else if (mod && e.key.toLowerCase() === 'a') { stop(); sel.current = new Set(tune().notes.map((_, i) => i)); rerender(); }
      else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && ids.length) { stop(); const dm = e.key === 'ArrowUp' ? 1 : -1; edit('Note verschieben', (t) => { for (const i of ids) t.notes[i].to = Math.round(t.notes[i].to) + dm; }); }
      else if ((e.key === 'Backspace' || e.key === 'Delete') && ids.length) { stop(); reset(); }
      else if (!mod && e.key.toLowerCase() === 'k') { stop(); correct(); }
      else if (!mod && e.key.toLowerCase() === 's') { stop(); split(); }
      else if (!mod && e.key.toLowerCase() === 'j') { stop(); join(); }
      else if (!mod && e.key.toLowerCase() === 'b') { stop(); bypass(); }
    };
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('pointerdown', onPointer, true); window.removeEventListener('keydown', onKey, true); pianoFocus.active = false; };
  });

  if (!clip || !track) return null;
  const tn = tune(), count = tn.notes.length, moved = tn.notes.filter((n) => !n.off && Math.abs(n.to - n.m) > 0.02).length;
  const big = tn.notes.filter((n) => !n.off && Math.abs(n.to - n.m) > 2).length;
  // Melodyne-Info: gewählte Note, nächster Halbton und Abweichung in Cent
  const one = sel.current.size === 1 ? tn.notes[[...sel.current][0]] : undefined, ro = refOffset(ref);
  const info = one ? (() => { const near = Math.round(one.m - ro), dc = Math.round((one.m - ro - near) * 100); return ` · Auswahl: ${noteName(near)} ${dc >= 0 ? '+' : ''}${dc} Cent${Math.abs(one.to - one.m) > 0.02 ? ` → ${noteName(Math.round(one.to - ro))}` : ''}${one.off ? ' (ausgelassen)' : ''}`; })() : '';
  return (
    <Panel className="pr-panel" title={<>Vocal-Tuning <span className="t2" style={{ fontFamily: 'var(--font)', fontWeight: 400, fontSize: 13 }}>{clip.name} · {track.name} · {detected === null && !clip.tune ? 'erkenne …' : `${count} Noten${moved ? `, ${moved} korrigiert` : ''}`} · {keyLabel(key)}{info}{big ? ` · ⚠ ${big} ${big === 1 ? 'Note' : 'Noten'} über 2 Halbtöne verschoben, Artefakte möglich` : ''}</span></>} right={<button className="btn small" onClick={onClose} title="Esc">Schließen</button>}>
      <div className="tune-tools">
      <label className="prl" title="Voreinstellung wählen und gleich korrigieren">Voreinstellung
        <select className="input" value="" onChange={(e) => { if (e.target.value) preset(e.target.value as keyof typeof TUNE_PRESETS); }}>
          <option value="">wählen …</option>
          {Object.entries(TUNE_PRESETS).map(([id, pr]) => <option key={id} value={id}>{pr.label}</option>)}
        </select>
      </label>
      <label className="prl" title="Worauf korrigiert wird. Standard wie bei Auto-Tune und Melodyne: nächster Halbton">Ziel
        <select className="input" value={!keyOpt || keyOpt === 'chromatic' ? '' : keyOpt} onChange={(e) => edit('Ziel fürs Tuning', (t) => { if (e.target.value) t.key = e.target.value; else delete t.key; })}>
          <option value="">Nächster Halbton</option>
          <option value="song">Tonart des Songs ({songKey})</option>
          {est && est.key !== songKey && est.score > 0.55 && <option value={est.key}>Erkannte Tonart ({est.key})</option>}
          {keyOpt && !['chromatic', 'song', est?.key].includes(keyOpt) && <option value={keyOpt}>{keyOpt}</option>}
        </select>
      </label>
      <label className="prl">Stärke
        <select className="input" value={strength} onChange={(e) => setStrength(Number(e.target.value))}>{[100, 90, 80, 75, 50, 25].map((v) => <option key={v} value={v}>{v} %</option>)}</select>
      </label>
      <button className="btn small primary" onClick={() => correct()} title="Taste K. Gewählte Noten (oder alle) auf das Ziel ziehen">Korrigieren</button>
      <label className="prl" title="Flex-Tune: Noten, die weiter vom Ziel liegen, bleiben unverändert (gewollte Slides und Blue Notes)">Nur bis
        <select className="input" value={tn.flex ?? 0} onChange={(e) => setOpt('flex', Number(e.target.value))}>{[0, 30, 50, 70, 100].map((v) => <option key={v} value={v}>{v ? `${v} Cent` : 'alle'}</option>)}</select>
      </label>
      <label className="prl" title="Langsames Wegdriften innerhalb einer Note geradeziehen">Drift
        <select className="input" value={Math.round(tn.drift * 100)} onChange={(e) => setOpt('drift', Number(e.target.value) / 100)}>{[0, 25, 50, 75, 100].map((v) => <option key={v} value={v}>{v} %</option>)}</select>
      </label>
      <label className="prl" title="0 % lässt das Vibrato, wie es ist">Vibrato glätten
        <select className="input" value={Math.round(tn.vibrato * 100)} onChange={(e) => setOpt('vibrato', Number(e.target.value) / 100)}>{[0, 25, 50, 75, 100].map((v) => <option key={v} value={v}>{v} %</option>)}</select>
      </label>
      <label className="prl" title="Wie schnell die Tonhöhe zwischen zwei Noten wechselt. 0 ms klingt wie hartes Auto-Tune.">Übergang
        <select className="input" value={tn.speed} onChange={(e) => setOpt('speed', Number(e.target.value))}>{[0, 20, 40, 80, 150].map((v) => <option key={v} value={v}>{v} ms</option>)}</select>
      </label>
      <label className="prl" title="Intervalle halten: zwei direkt aufeinanderfolgende Noten werden nicht gegeneinander gezogen, das gesungene Intervall ändert sich höchstens um diesen Wert">Intervalle
        <select className="input" value={tn.keep ?? 0} onChange={(e) => setOpt('keep', Number(e.target.value))}>{[0, 15, 25, 40].map((v) => <option key={v} value={v}>{v ? `± ${v} Cent` : 'frei'}</option>)}</select>
      </label>
      <label className="prl" title="Humanize: lange Noten werden langsamer gezogen, Einsatz und Ausklang bleiben natürlich">Humanize
        <select className="input" value={Math.round((tn.humanize ?? 0) * 100)} onChange={(e) => setOpt('humanize', Number(e.target.value) / 100)}>{[0, 30, 60, 100].map((v) => <option key={v} value={v}>{v} %</option>)}</select>
      </label>
      <label className="prl" title="Kammerton der Aufnahme">A =
        <select className="input" value={ref} onChange={(e) => setOpt('ref', Number(e.target.value))}>{[432, 438, 440, 442, 444].map((v) => <option key={v} value={v}>{v} Hz</option>)}</select>
      </label>
      <button className="btn small" onClick={reset} title="⌫: gewählte Noten (oder alle) wieder auf die gesungene Tonhöhe">Zurücksetzen</button>
      <button className="btn small" onClick={bypass} title="B: gewählte Noten von der Korrektur ausnehmen (Bypass) bzw. wieder einbeziehen">Auslassen</button>
      <button className="btn small" onClick={split} title="S: Note an der Abspielposition teilen">Teilen</button>
      <button className="btn small" onClick={join} title="J: gewählte benachbarte Noten verbinden">Verbinden</button>
      <button className="btn small" onClick={toMidi} title="Melodie als neue MIDI-Spur (z. B. für ein Instrument oder Noten)">Als MIDI</button>
      <button className="btn small" onClick={redetect} title="Noten neu erkennen (z. B. nach Schnitten), setzt die Korrektur zurück">Neu erkennen</button>
      </div>
      <div className="pr" ref={wrap} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onWheel={onWheel} onDoubleClick={onDouble} onContextMenu={(e) => e.preventDefault()}>
        <canvas ref={canvas} />
      </div>
      <div className="pr-hint">
        Blöcke: gesungene Noten (gestrichelt: wo sie lagen, rot: über 2 Halbtöne verschoben, grau: ausgelassen) · graue Linie: gesungen, farbige: nach der Korrektur · Note ziehen rastet auf Halbtöne, ⌥ frei in Cent · Doppelklick: aufs Ziel · ↑ ↓ Halbton · ⇧ oder Rahmen wählt mehrere · K korrigieren · B auslassen · S teilen (Abspielposition) · J verbinden · ⌫ zurücksetzen · ⌘-Mausrad zoomt
      </div>
    </Panel>
  );
}

function rr(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, w / 2, h / 2)); g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}
function shade(hex: string, f: number) { const n = parseInt(hex.slice(1), 16); return `rgb(${Math.round((n >> 16) * f)},${Math.round(((n >> 8) & 255) * f)},${Math.round((n & 255) * f)})`; }
function hexA(hex: string, a: number) { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`; }
